import clsx from "clsx";
import {
  AlertCircle,
  BriefcaseBusiness,
  Clock3,
  FolderOpen,
  UserCheck,
  UserRound,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";

import { Badge, Spinner } from "../../../../ui/index.ts";
import { TruncatedCellTooltip } from "../../../../ui/responsive-table/truncated-cell-tooltip.tsx";
import { Card } from "../../../../shared/card/card.tsx";
import type {
  ProjectReportDesignationGroup,
  ProjectReportPerson,
  ProjectUtilizationReport as ProjectUtilizationReportData,
} from "../../types.ts";

import styles from "./project-utilization-report.module.css";

type ReportStatus = "idle" | "incomplete-range" | "loading" | "error" | "ready";

export interface ProjectUtilizationReportProps {
  status: ReportStatus;
  error: string | null;
  data: ProjectUtilizationReportData | null;
  selectedDurationLabel?: string;
}

function formatText(value: unknown, fallback = "-") {
  if (value == null) return fallback;
  const text = String(value).trim();
  return text || fallback;
}

function formatDate(value: string | null | undefined) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatMinutes(value: number | null | undefined) {
  const totalMinutes = Math.max(0, Math.round(Number(value ?? 0)));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

function minutesFromHours(value: number | null | undefined) {
  return Math.round(Number(value ?? 0) * 60);
}

function formatLoggedTime(
  minutes: number | null | undefined,
  hours?: number | null,
) {
  return formatMinutes(minutes ?? minutesFromHours(hours));
}

function formatDesignation(value: string | null | undefined) {
  const text = formatText(value, "Unassigned");
  return text
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatMemberType(value: string | null | undefined) {
  const text = formatText(value, "MAIN").toUpperCase();
  if (text === "SHADOW") return "Shadow Employee";
  if (text === "MAIN") return "Main Employee";
  return formatDesignation(text);
}

function formatTimesheetAutoApproved(needsApproval: boolean | null | undefined) {
  return needsApproval === false ? "Yes" : "No";
}

function EmptyState({
  title,
  message,
  tone = "default",
}: {
  title: string;
  message: string;
  tone?: "default" | "danger";
}) {
  const Icon = tone === "danger" ? AlertCircle : FolderOpen;
  return (
    <Card padding="5xl" className={styles.emptyCard}>
      <span className={clsx(styles.emptyIcon, tone === "danger" && styles.emptyIconDanger)}>
        <Icon size={50} strokeWidth={1.75} aria-hidden />
      </span>
      <h3 className={styles.emptyTitle}>{title}</h3>
      <p className={styles.emptyMessage}>{message}</p>
    </Card>
  );
}

function SummaryGrid({ data }: { data: ProjectUtilizationReportData }) {
  const summary = data.summary;

  return (
    <div className={styles.summaryGrid}>
      <SummaryCard
        label="Total Members"
        value={summary?.totalMembers ?? 0}
        tone="blue"
        icon={<Users size={20} strokeWidth={2} aria-hidden />}
      />
      <SummaryCard
        label="Main Members"
        value={summary?.totalMainMembers ?? 0}
        tone="amber"
        icon={<UserCheck size={22} strokeWidth={2} aria-hidden />}
      />
      <SummaryCard
        label="Shadow Members"
        value={summary?.totalShadowMembers ?? 0}
        tone="green"
        icon={<UserRound size={22} strokeWidth={2} aria-hidden />}
      />
      <SummaryCard
        label="Total Logged Time"
        value={formatLoggedTime(summary?.totalLoggedMinutes, summary?.totalLoggedHours)}
        tone="red"
        icon={<Clock3 size={22} strokeWidth={2} aria-hidden />}
      />
    </div>
  );
}

function RoleCard({ label, person }: { label: string; person?: ProjectReportPerson | null }) {
  const employeeName = person?.employeeName?.trim();

  if (!employeeName) return null;

  return (
    <div className={styles.roleCard}>
      <span className="sub-heading-md text-accent">{label}</span>
      <strong className={`sub-heading-md ${styles.roleName}`}>{employeeName}</strong>

    </div>
  );
}

function ProjectMetaCard({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.roleCard}>
      <span className="sub-heading-md text-accent">{label}</span>
      <strong className={`sub-heading-md ${styles.roleName}`}>{value}</strong>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: ReactNode;
  icon: ReactNode;
  tone: "blue" | "amber" | "green" | "red";
}) {
  return (
    <div className={styles.summaryCard}>
      <span className={clsx(styles.summaryIcon, styles[`summaryIcon${tone}`])}>
        {icon}
      </span>
      <div className={styles.summaryContent}>
        <span className="sub-heading-sm">{label}</span>
        <p className="heading-lg">{value}</p>
      </div>
    </div>
  );
}

export function ProjectDetails({ data }: { data: ProjectUtilizationReportData }) {
  const project = data.project;

  return (
    <Card padding="5xl" className={styles.projectCard}>
      <div className={styles.projectHeader}>
        <div className={styles.projectTitleBlock}>
          <span className={styles.projectIcon}>
            <BriefcaseBusiness size={24} strokeWidth={2} aria-hidden />
          </span>

          <h2 className={clsx("heading-lg", styles.projectTitle)}>
            {formatText(project?.projectName, "Selected Project")}
          </h2>


        </div>

      </div>

      <div className={styles.roleGrid}>
        <ProjectMetaCard
          label="Project Type"
          value={formatDesignation(project?.projectType)}
        />
        <RoleCard label="Principal Sponsor" person={project?.ps} />
        <RoleCard label="Delivery Lead" person={project?.dl} />
        <RoleCard label="Project Manager" person={project?.pm} />
        <ProjectMetaCard
          label="SOW Start Date"
          value={formatDate(project?.sowStartDate)}
        />

        <ProjectMetaCard
          label="Actual Start Date"
          value={formatDate(project?.startDate)}
        />
        <ProjectMetaCard
          label="SOW End Date"
          value={formatDate(project?.sowEndDate)}
        />
        <ProjectMetaCard
          label="Actual End Date"
          value={formatDate(project?.endDate)}
        />
        <RoleCard label="Program Manager" person={project?.programManager} />
        <RoleCard label="Lead Business Analyst" person={project?.lba} />
        <ProjectMetaCard
          label="Timesheet Auto-Approved"
          value={formatTimesheetAutoApproved(project?.needsApproval)}
        />

      </div>
    </Card>
  );
}

function DesignationGroupTable({
  groups,
  selectedDurationLabel,
}: {
  groups: ProjectReportDesignationGroup[];
  selectedDurationLabel?: string;
}) {
  const totalMinutes = groups.reduce(
    (sum, group) => sum + Number(group.totalMinutes ?? minutesFromHours(group.totalHours)),
    0,
  );
  return (
    <Card padding="none" className={styles.tableCard}>

      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <colgroup>
            <col className={styles.serialColumn} />
            <col className={styles.employeeColumn} />
            <col className={styles.timeColumn} />
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>Employee Name</th>
              <th className={styles.numeric}>Total Logged Time</th>
            </tr>
          </thead>
          {groups.map((group, groupIndex) => (
            <tbody key={formatDesignation(group.designation)} className={styles.groupBody}>
              <tr className={styles.groupRow}>
                <th colSpan={3}>
                  <div className={styles.groupHeaderInner}>
                    <span className={styles.groupName}>
                      {formatDesignation(group.designation)}
                    </span>
                    <span className={styles.groupBadges}>
                      <Badge variant="neutral" className={styles.countBadge}>
                        Members: {group.employees?.length ?? 0}
                      </Badge>
                      <Badge variant="warning" className={styles.countBadge}>
                        Time: {formatLoggedTime(group.totalMinutes, group.totalHours)}
                      </Badge>
                    </span>
                  </div>
                </th>
              </tr>
              {(group.employees ?? []).map((employee, employeeIndex) => (
                <tr key={`${employee.employeeId}-${employee.employeeName}`}>
                  <td className={styles.serialCell}>{employeeIndex + 1}</td>
                  <td className={styles.employeeCell}>
                    <span className={styles.employeeName}>
                      <TruncatedCellTooltip
                        tooltipText={formatText(employee.employeeName)}
                        truncatedClassName={styles.employeeNameText}
                      >
                        {formatText(employee.employeeName)}
                      </TruncatedCellTooltip>
                      {employee.memberType?.toUpperCase() === "SHADOW" ? (
                        <Badge variant="success" className={styles.memberTypeBadge}>
                          {formatMemberType(employee.memberType)}
                        </Badge>
                      ) : null}
                    </span>
                  </td>
                  <td className={styles.numeric}>
                    {formatLoggedTime(employee.loggedMinutes, employee.loggedHours)}
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
          <tfoot>
            <tr className={styles.totalRow}>
              <th colSpan={2}>
                {selectedDurationLabel ? (
                  <span className={styles.totalMeta}>
                    <span className={styles.totalMetaLabel}>Selected Duration:</span>{" "}
                    {selectedDurationLabel}
                  </span>
                ) : null}
              </th>
              <td className={clsx(styles.numeric, styles.totalTime)}>
                <span className={styles.totalCount}>Total Time&nbsp;</span>
                {formatMinutes(totalMinutes)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}

export function ProjectUtilizationReport({
  status,
  error,
  data,
  selectedDurationLabel,
}: ProjectUtilizationReportProps) {
  if (status === "idle") {
    return (
      <EmptyState
        title="Please select project to view details"
        message=""
      />
    );
  }

  if (status === "incomplete-range") {
    return (
      <EmptyState
        title="Select a complete date range"
        message="For Custom view, select both start and end date to load project data."
      />
    );
  }

  if (status === "loading") {
    return (
      <Card padding="5xl" className={styles.loadingCard}>
        <Spinner variant="inline" />
        <span className="body-sm">Loading...</span>
      </Card>
    );
  }

  if (status === "error") {
    return (
      <EmptyState
        title="Unable to load data"
        message={ ""}
        tone="danger"
      />
    );
  }

  const groups = data?.designations ?? [];

  if (!data || groups.length === 0) {
    return (
      <>
        {data ? (
          <div className={styles.reportStack}>
            <SummaryGrid data={data} />
            <ProjectDetails data={data} />
          </div>
        ) : null}
        <EmptyState
          title="No data found"
          message="There are no logged hours for the selected project and period."
        />
      </>
    );
  }

  return (
    <div className={styles.reportStack}>
      <SummaryGrid data={data} />
      <ProjectDetails data={data} />
      <DesignationGroupTable
        groups={groups}
        selectedDurationLabel={selectedDurationLabel}
      />
    </div>
  );
}
